/* eslint-disable ember/no-runloop */
import { action } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import type { ComparisonSet } from 'frontend-utils/utils/constants/benchmark-insight';
import * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import type { CustomLegend } from 'lumen/components/hotel/benchmark-insight/charts/legend-items/legend-item';
import HotelBenchmarkInsightChartsLegendItemsLegendItem from 'lumen/components/hotel/benchmark-insight/charts/legend-items/legend-item';
import type { BeniTooltipData } from 'lumen/components/hotel/benchmark-insight/tooltips/beni-tooltip';
import HotelBenchmarkInsightTooltipsBeniTooltip from 'lumen/components/hotel/benchmark-insight/tooltips/beni-tooltip';
import type { BeniBreakdown, BeniMetaComparisonSet } from 'lumen/models';
import {
  BREAKDOWN_PERIOD_TYPES,
  type BreakdownPeriodType,
} from 'lumen/models/benchmark-insight/breakdown';
import {
  BENI_CHARTS_AXIS_TITLE_COLOR,
  BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS,
  BENI_CHARTS_GRID_LINE_COLOR,
  BENI_CHARTS_HEIGHT,
  BENI_CHARTS_LABEL_COLOR,
  BENI_CHARTS_Y_AXIS_LABEL_MIN_WIDTH,
  BENI_COMPARISON_SET_LONG_TRANSLATIONS,
  COLOR_BENCHMARK_INSIGHT_DATASET_OWN,
  COLOR_BENCHMARK_INSIGHT_DATASET_PRIMARY,
  COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_OWN,
  COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_PRIMARY,
  COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_SECONDARY,
  COLOR_BENCHMARK_INSIGHT_DATASET_SECONDARY,
  type Kpi,
  KPI_LONG_TRANSLATIONS,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import {
  PACE_CHART_SERIES_IDS,
  type PaceChartSeries,
  type PaceChartSeriesId,
  type PaceChartSeriesPoint,
} from 'lumen/utils/constants/benchmark-insight/beni-pace-chart-constant-values';
import type { DateRangeType } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import {
  DATE_AGGREGATION,
  type DateAggregationType,
} from 'lumen/utils/constants/general-constants';
import { KPIS } from 'lumen/utils/constants/ri-constant-values';

interface HotelBenchmarkInsightChartsPaceChartSignature {
  Args: {
    dateAggregation: DateAggregationType;
    extractDate: string;
    ownComparisonSetBreakdowns: BeniBreakdown[];
    ownComparisonSetMeta: BeniMetaComparisonSet | null;
    primaryComparisonSetBreakdowns: BeniBreakdown[];
    primaryComparisonSetMeta: BeniMetaComparisonSet | null;
    referencePeriod: ReferencePeriod;
    secondaryComparisonSetBreakdowns: BeniBreakdown[];
    secondaryComparisonSetMeta: BeniMetaComparisonSet | null;
    selectedKpi: Kpi;
  };
}

export default class HotelBenchmarkInsightChartsPaceChart extends Component<HotelBenchmarkInsightChartsPaceChartSignature> {
  @service declare access: Services['access'];
  @service('benchmark-insight/beni-chart-service')
  declare benchmarkInsightBeniChartService: Services['benchmark-insight/beni-chart-service'];
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked tooltipData: BeniTooltipData | null = null;
  @tracked visibleSeries: PaceChartSeriesId[] = Object.values(
    PACE_CHART_SERIES_IDS,
  );

  TOOLTIP_DUMMY_ELEMENT_ID = 'tooltip-pace-chart-dummy-div';

  visibleKpis: Kpi[] = [KPIS.OCCUPANCY, KPIS.ADR, KPIS.REVPAR];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
      ownComparisonSetBreakdowns,
      primaryComparisonSetBreakdowns,
      secondaryComparisonSetBreakdowns,
    } = this.args;

    const series: PaceChartSeries[] = [];

    this.pushSeriesForComparisonSet(
      series,
      ownComparisonSetMeta,
      ownComparisonSetBreakdowns,
      PACE_CHART_SERIES_IDS.OWN,
      PACE_CHART_SERIES_IDS.OWN_REFERENCE,
      COLOR_BENCHMARK_INSIGHT_DATASET_OWN,
      COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_OWN,
    );

    this.pushSeriesForComparisonSet(
      series,
      primaryComparisonSetMeta,
      primaryComparisonSetBreakdowns,
      PACE_CHART_SERIES_IDS.PRIMARY_COMPARISON_SET,
      PACE_CHART_SERIES_IDS.PRIMARY_COMPARISON_SET_REFERENCE,
      COLOR_BENCHMARK_INSIGHT_DATASET_PRIMARY,
      COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_PRIMARY,
    );

    this.pushSeriesForComparisonSet(
      series,
      secondaryComparisonSetMeta,
      secondaryComparisonSetBreakdowns,
      PACE_CHART_SERIES_IDS.SECONDARY_COMPARISON_SET,
      PACE_CHART_SERIES_IDS.SECONDARY_COMPARISON_SET_REFERENCE,
      COLOR_BENCHMARK_INSIGHT_DATASET_SECONDARY,
      COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_SECONDARY,
    );

    return series;
  }

  get chartOptions(): Highcharts.Options {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const labelStyle = {
      color: BENI_CHARTS_LABEL_COLOR,
      minWidth: BENI_CHARTS_Y_AXIS_LABEL_MIN_WIDTH,
      x: -55,
    };

    return {
      chart: {
        height: BENI_CHARTS_HEIGHT,
        zoomType: undefined,
        animation: false,
      },
      yAxis: {
        title: {
          margin: 5,
          x: -9.5,
          text: this.intl.t(KPI_LONG_TRANSLATIONS[that.args.selectedKpi]),
          style: {
            color: BENI_CHARTS_AXIS_TITLE_COLOR,
          },
        },
        labels: {
          style: labelStyle,
          formatter(): string {
            const value = this.value as number;

            return that.benchmarkInsightBeniChartService.yAxisLabelFormatter(
              value,
              that.args.selectedKpi === KPIS.OCCUPANCY,
            );
          },
        },
        gridLineColor: BENI_CHARTS_GRID_LINE_COLOR,
      },
      xAxis: {
        type: 'datetime',
        labels: {
          style: labelStyle,
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            const value = this.value as number;

            if (!value) {
              return '';
            }

            const dateFormatted = Highcharts.dateFormat(
              that.dates.highchartsLabelFormats[DATE_AGGREGATION.DAY],
              value,
            );

            return dateFormatted;
          },
        },
        plotLines: this.xAxisPlotLines,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          lineWidth: 2,
          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
        },
      },
      tooltip: {
        padding: 0,
        borderWidth: 0,
        shadow: false,
        distance: 20,
        outside: true,
        shared: true,
        useHTML: true,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();
          that.tooltipData = that.tooltipDataDict[this.x] ?? null;
          end();

          return (
            document.getElementById(that.TOOLTIP_DUMMY_ELEMENT_ID)?.innerHTML ??
            null
          );
        },
      },
    };
  }

  get paceChartDateRange(): DateRangeType {
    const { ownComparisonSetBreakdowns } = this.args;

    // Since backend backfills the API with null values for dates with missing values, it's safe to check one comparison set only.
    // The `own` comparison set seems the safest.
    const paceSegmentationDates = ownComparisonSetBreakdowns.map(
      (paceSegmentation) => paceSegmentation.date,
    );

    return {
      start: dayjs.min(paceSegmentationDates) ?? dayjs(),
      end: dayjs.max(paceSegmentationDates) ?? dayjs(),
    };
  }

  // Memoization of the tooltip data.
  // Instead of recalculating the tooltip data in the tooltip formatter function, we calculate it once and store it in this dict.
  // The cached decorator is needed to make sure the getter is not recalculated on hover, as this greatly reduces performance.
  @cached
  get tooltipDataDict(): Record<number, BeniTooltipData> {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    } = this.args;

    const dict = {};

    if (ownComparisonSetMeta) {
      this.addComparisonSetBreakdownsToTooltipDataDict(
        dict,
        ownComparisonSetMeta,
        'ownComparisonSetBreakdown',
      );
    }

    if (primaryComparisonSetMeta) {
      this.addComparisonSetBreakdownsToTooltipDataDict(
        dict,
        primaryComparisonSetMeta,
        'primaryComparisonSetBreakdown',
      );
    }

    if (secondaryComparisonSetMeta) {
      this.addComparisonSetBreakdownsToTooltipDataDict(
        dict,
        secondaryComparisonSetMeta,
        'secondaryComparisonSetBreakdown',
      );
    }

    return dict;
  }

  get visibleCustomLegends(): CustomLegend[] {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    } = this.args;

    const visibleCustomLegends: CustomLegend[] = [];

    this.pushCustomLegendForComparisonSet(
      visibleCustomLegends,
      ownComparisonSetMeta,
      PACE_CHART_SERIES_IDS.OWN,
      PACE_CHART_SERIES_IDS.OWN_REFERENCE,
      BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.OWN,
      BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.OWN_REFERENCE,
    );

    this.pushCustomLegendForComparisonSet(
      visibleCustomLegends,
      primaryComparisonSetMeta,
      PACE_CHART_SERIES_IDS.PRIMARY_COMPARISON_SET,
      PACE_CHART_SERIES_IDS.PRIMARY_COMPARISON_SET_REFERENCE,
      BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.PRIMARY,
      BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.PRIMARY_REFERENCE,
    );

    this.pushCustomLegendForComparisonSet(
      visibleCustomLegends,
      secondaryComparisonSetMeta,
      PACE_CHART_SERIES_IDS.SECONDARY_COMPARISON_SET,
      PACE_CHART_SERIES_IDS.SECONDARY_COMPARISON_SET_REFERENCE,
      BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.SECONDARY,
      BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.SECONDARY_REFERENCE,
    );

    this.benchmarkInsightBeniService.addUnavailableComparisonSetDisabledTooltip(
      visibleCustomLegends,
    );

    return visibleCustomLegends;
  }

  get xAxisPlotLines(): Highcharts.XAxisPlotLinesOptions[] {
    const { extractDate } = this.args;
    const { paceChartDateRange } = this;

    const plotLines: Highcharts.XAxisPlotLinesOptions[] = [];

    if (!extractDate || !paceChartDateRange) {
      return plotLines;
    }

    const extractDateLocalTime =
      this.hotelAndMarketService.toLocalTime(extractDate);

    if (
      extractDateLocalTime.isBetween(
        paceChartDateRange.start,
        paceChartDateRange.end,
      ) ||
      extractDateLocalTime.isSame(paceChartDateRange.start) ||
      extractDateLocalTime.isSame(paceChartDateRange.end)
    ) {
      const plotLineExtractDate: Highcharts.XAxisPlotLinesOptions = {
        color: 'var(--prism-color-interactive-border-neutral-hover)',
        dashStyle: 'Dash',
        value: this.hotelAndMarketService.toLocalUnixTime(
          extractDateLocalTime.unix(),
        ),
      };

      plotLines.push(plotLineExtractDate);
    }

    return plotLines;
  }

  addComparisonSetBreakdownsToTooltipDataDict(
    dict: Record<number, BeniTooltipData>,
    comparisonSetMeta: BeniMetaComparisonSet,
    key:
      | 'ownComparisonSetBreakdown'
      | 'primaryComparisonSetBreakdown'
      | 'secondaryComparisonSetBreakdown',
  ): void {
    const { chartData, benchmarkInsightBeniChartService } = this;

    const comparisonSetSeries =
      benchmarkInsightBeniChartService.getSeriesByComparisonSetType(
        chartData,
        comparisonSetMeta.comparisonSetType,
      );

    comparisonSetSeries?.data.forEach((comparisonSetSeriesPoint) => {
      const xValue = comparisonSetSeriesPoint.x;
      const comparisonSetBreakdown =
        comparisonSetSeriesPoint.segmentation ?? null;
      const existingDictEntry = dict[xValue];

      if (existingDictEntry) {
        existingDictEntry[key] = comparisonSetBreakdown;
      } else {
        const tooltipData: BeniTooltipData = {
          ownComparisonSetBreakdown: null,
          primaryComparisonSetBreakdown: null,
          secondaryComparisonSetBreakdown: null,
          date: dayjs.utc(xValue),
          dateAggregation: DATE_AGGREGATION.DAY,
        };
        tooltipData[key] = comparisonSetBreakdown;

        dict[xValue] = tooltipData;
      }
    });
  }

  createCustomLegend(
    comparisonSetMeta: BeniMetaComparisonSet,
    chartSeriesId: PaceChartSeriesId,
    isReference = false,
    cssSelector: string,
  ): CustomLegend {
    const { comparisonSetType, isVisible } = comparisonSetMeta;

    const isToggledOn = this.visibleSeries.includes(chartSeriesId);
    const label = isReference
      ? this.benchmarkInsightBeniService.getComparisonSetReferencePeriodShortTranslation(
          comparisonSetType,
          this.args.referencePeriod,
        )
      : this.intl.t(BENI_COMPARISON_SET_LONG_TRANSLATIONS[comparisonSetType]);

    return {
      id: chartSeriesId,
      cssSelector,
      type: 'line',
      isHidden: !isToggledOn,
      isDisabled: !isVisible,
      label,
      breakdownPeriodType: isReference
        ? BREAKDOWN_PERIOD_TYPES.REFERENCE
        : BREAKDOWN_PERIOD_TYPES.CURRENT,
      isDisabledForUnavailableComparisonSet: !isVisible,
    };
  }

  createSeries(
    comparisonSetType: ComparisonSet,
    chartSeriesId: PaceChartSeriesId,
    isReference = false,
    color: string,
  ): PaceChartSeries {
    const isVisible = this.visibleSeries.includes(chartSeriesId);
    const name = isReference
      ? this.benchmarkInsightBeniService.getComparisonSetReferencePeriodShortTranslation(
          comparisonSetType,
          this.args.referencePeriod,
        )
      : this.intl.t(BENI_COMPARISON_SET_LONG_TRANSLATIONS[comparisonSetType]);

    return {
      name,
      type: 'line',
      color,
      data: [],
      visible: isVisible,
      marker: {
        enabled: false,
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getXValueForSegmentation(segmentation: BeniBreakdown) {
    return this.hotelAndMarketService.toLocalUnixTime(segmentation.date.unix());
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isReferenceDataAvailable(comparisonSetMeta: BeniMetaComparisonSet) {
    return (
      this.access.hasBenchmarkInsightReferencePeriodData &&
      comparisonSetMeta.hasReferencePeriodData
    );
  }

  mapBreakdownToDataPoint(
    segmentation: BeniBreakdown,
    periodType: BreakdownPeriodType = BREAKDOWN_PERIOD_TYPES.CURRENT,
  ): PaceChartSeriesPoint {
    const breakdownPeriod =
      segmentation.getBreakdownPeriodByBreakdownPeriodType(periodType);

    const dataPoint = {
      x: this.getXValueForSegmentation(segmentation),
      y: breakdownPeriod.total[this.args.selectedKpi],
      segmentation, // Add the segmentation to the data point, so we can access it in the tooltip formatter.
    };

    return dataPoint;
  }

  // eslint-disable-next-line max-params
  pushCustomLegendForComparisonSet(
    customLegends: CustomLegend[],
    comparisonSetMeta: BeniMetaComparisonSet | null,
    chartIdCurrentPeriod: PaceChartSeriesId,
    chartIdReferencePeriod: PaceChartSeriesId,
    cssSelector: string,
    cssSelectorReference: string,
  ): void {
    if (!comparisonSetMeta) {
      return;
    }

    const customLegendCurrentPeriod = this.createCustomLegend(
      comparisonSetMeta,
      chartIdCurrentPeriod,
      false,
      cssSelector,
    );

    customLegends.push(customLegendCurrentPeriod);

    if (this.isReferenceDataAvailable(comparisonSetMeta)) {
      const customLegendReferencePeriod = this.createCustomLegend(
        comparisonSetMeta,
        chartIdReferencePeriod,
        true,
        cssSelectorReference,
      );

      customLegends.push(customLegendReferencePeriod);
    }
  }

  // eslint-disable-next-line max-params
  pushSeriesForComparisonSet(
    series: PaceChartSeries[],
    comparisonSetMeta: BeniMetaComparisonSet | null,
    comparisonSetBreakdowns: BeniBreakdown[],
    chartIdCurrentPeriod: PaceChartSeriesId,
    chartIdReferencePeriod: PaceChartSeriesId,
    color: string,
    referenceColor: string,
  ): void {
    if (comparisonSetMeta && comparisonSetBreakdowns) {
      if (!comparisonSetMeta) {
        return;
      }

      const { comparisonSetType } = comparisonSetMeta;

      const comparisonSetSeriesCurrentPeriod = this.createSeries(
        comparisonSetType,
        chartIdCurrentPeriod,
        false,
        color,
      );

      comparisonSetSeriesCurrentPeriod.data = comparisonSetBreakdowns.map(
        (comparisonSetBreakdown) =>
          this.mapBreakdownToDataPoint(comparisonSetBreakdown),
      );

      series.push(comparisonSetSeriesCurrentPeriod);

      if (this.isReferenceDataAvailable(comparisonSetMeta)) {
        const comparisonSetSeriesReferencePeriod = this.createSeries(
          comparisonSetType,
          chartIdReferencePeriod,
          true,
          referenceColor,
        );

        comparisonSetSeriesReferencePeriod.data = comparisonSetBreakdowns.map(
          (comparisonSetBreakdown) =>
            this.mapBreakdownToDataPoint(
              comparisonSetBreakdown,
              BREAKDOWN_PERIOD_TYPES.REFERENCE,
            ),
        );

        series.push(comparisonSetSeriesReferencePeriod);
      }
    }
  }

  @action
  toggleSeries(chartSeriesId: PaceChartSeriesId): void {
    const { visibleSeries } = this;

    const usageTrackingTraits = {
      eventTrigger: 'pace',
      product: 'benchmark-insight',
      series: chartSeriesId,
    };

    if (visibleSeries.includes(chartSeriesId)) {
      this.visibleSeries = visibleSeries.filter(
        (visibleSeriesId) => visibleSeriesId !== chartSeriesId,
      );

      this.usageAnalytics.track('Data Series Hidden', {
        ...usageTrackingTraits,
      });
    } else {
      this.visibleSeries = visibleSeries.concat(chartSeriesId);

      this.usageAnalytics.track('Data Series Shown', {
        ...usageTrackingTraits,
      });
    }
  }

  <template>
    <GeneralChartsOtaChart
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
    />

    <div hidden id={{this.TOOLTIP_DUMMY_ELEMENT_ID}}>
      {{#if this.tooltipData}}
        <HotelBenchmarkInsightTooltipsBeniTooltip
          @ownComparisonSetMeta={{@ownComparisonSetMeta}}
          @primaryComparisonSetMeta={{@primaryComparisonSetMeta}}
          @referencePeriod={{@referencePeriod}}
          @secondaryComparisonSetMeta={{@secondaryComparisonSetMeta}}
          @selectedKpi={{@selectedKpi}}
          @selectedRange={{this.paceChartDateRange}}
          @showMix={{false}}
          @tooltipData={{this.tooltipData}}
          @visibleKpis={{this.visibleKpis}}
        />
      {{/if}}
    </div>

    <div class="chart-legend">
      {{#each this.visibleCustomLegends as |legend|}}
        <HotelBenchmarkInsightChartsLegendItemsLegendItem
          @legend={{legend}}
          @toggleSeries={{this.toggleSeries}}
        />
      {{/each}}
    </div>
  </template>
}
